import { useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import CommonDialog from "../common/CommonDialog/CommonDialog";

const REJECT_REASONS = [
  "Duplicate patient",
  "Bad / mismatched data",
  "Not our patient",
  "Test / junk record",
  "Other",
] as const;

const OTHER = "Other";

function composeComment(reason: string, freeText: string): string {
  const extra = freeText.trim();
  if (reason === OTHER) return extra;
  return extra ? `${reason} — ${extra}` : reason;
}

type RejectPatientModalProps = {
  isOpen: boolean;
  patientName: string;
  onClose: () => void;
  onSubmit: (comment: string) => void;
  isSubmitting: boolean;
};

export default function RejectPatientModal({
  isOpen,
  patientName,
  onClose,
  onSubmit,
  isSubmitting,
}: RejectPatientModalProps) {
  const [reason, setReason] = useState<string>(REJECT_REASONS[0]);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason(REJECT_REASONS[0]);
      setComment("");
    }
  }, [isOpen]);

  const isOther = reason === OTHER;
  const isSubmitDisabled = isSubmitting || (isOther && !comment.trim());

  return (
    <CommonDialog
      isDialogOpen={isOpen}
      onDialogClose={onClose}
      title="Reject Patient"
      maxWidth="xs"
      actionContent={
        <>
          <Button
            variant="outlined"
            onClick={onClose}
            data-testid="reject-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => onSubmit(composeComment(reason, comment))}
            disabled={isSubmitDisabled}
            data-testid="reject-modal-submit"
          >
            Reject
          </Button>
        </>
      }
    >
      <Box sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Provide a reason for rejecting {patientName || "this patient"}.
        </Typography>
        <TextField
          select
          fullWidth
          label="Reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{ htmlInput: { "data-testid": "reject-reason-select" } }}
        >
          {REJECT_REASONS.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          multiline
          rows={3}
          fullWidth
          required={isOther}
          label={isOther ? "Reason" : "Comment (optional)"}
          value={comment}
          onChange={e => setComment(e.target.value)}
          slotProps={{ htmlInput: { "data-testid": "reject-comment-input" } }}
        />
      </Box>
    </CommonDialog>
  );
}
